import { useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useMutation } from '@tanstack/react-query';
import type { Chat } from '@trinity/chat';
import { useChatApiClient } from '../../containers/Chat/useApiClient';
import { CancelAction } from '../presentation/Message.QueryHint/CancelAction';
import { AnalysisQueryHint } from '../presentation/Message.QueryHint/QueryHint';
import { ActiveChatSessionContext } from './context';
import { useCancelSegmentation } from './hooks/useCancelSegmentation';
import { SegmentationDimension } from './Segmentation.Dimension';

const MAX_DIMENSIONS = 10;

type Props = {
  queryId: number;
  queryIdentifier: string;
  value: Chat.QueryState.SegmentationQuestionsNeeded;
};

export const SegmentationDimensionSelection = (props: Props) => {
  const chat = useContext(ActiveChatSessionContext);
  const apiClient = useChatApiClient();

  // Initialize local state from props
  const [dimensions, setDimensions] = useState<string[]>(() =>
    props.value.dimensions.length > 0 ? props.value.dimensions : [''],
  );

  const dimensionSelectionMutation = useMutation({
    mutationFn: async (dimensionsList: string[]) => {
      const response = await apiClient.authenticatedClient.post<{
        error: boolean;
        citations: Chat.Citation[];
        value: Chat.QueryResponse;
        state?: { segmentationState: Chat.QueryState.SegmentationComplete };
      }>('/chat/segmentation/dimensions-selected', {
        queryIdentifier: props.queryIdentifier,
        chatIdentifier: chat.identifier,
        dimensions: dimensionsList,
      });
      return response.data;
    },
  });

  const handleDimensionChange = useCallback((id: number, value: string) => {
    setDimensions(prevDimensions =>
      prevDimensions.map((dimension, idx) => (idx === id ? value : dimension)),
    );
  }, []);

  const handleAddDimension = useCallback(() => {
    setDimensions(prevDimensions => [...prevDimensions, '']);
  }, []);

  const handleRemoveDimension = useCallback((idx: number) => {
    setDimensions(prevDimensions => prevDimensions.filter((_, index) => index !== idx));
  }, []);

  const handleContinue = useCallback(() => {
    const validDimensions = dimensions.filter(d => d.trim().length > 0);
    dimensionSelectionMutation.mutate(validDimensions);
  }, [dimensions, dimensionSelectionMutation]);

  const cancelMutation = useCancelSegmentation({ queryIdentifier: props.queryIdentifier });

  const handleCancel = useCallback(() => {
    return cancelMutation.mutateAsync();
  }, [cancelMutation]);

  const canContinue = useMemo(
    () => !dimensionSelectionMutation.isPending && dimensions.filter(d => d.trim().length > 0).length > 0,
    [dimensionSelectionMutation.isPending, dimensions],
  );

  const canRemoveDimension = dimensions.length > 1;
  const canAddDimension = dimensions.length < MAX_DIMENSIONS;

  return (
    <Root>
      <HeaderCopy>
        Ok, great! What topics or questions would you like to analyze?
      </HeaderCopy>
      <PickCopy>
        Input up to {MAX_DIMENSIONS} dimensions of the analysis.
      </PickCopy>
      <Dimensions>
        {dimensions.map((value, idx) => (
          <DimensionRow key={idx}>
            <SegmentationDimension
              id={idx}
              value={value}
              onChange={handleDimensionChange}
              readonly={false}
              className="dimension-input"
            />
            {canRemoveDimension && (
              <DimensionRemove onClick={() => handleRemoveDimension(idx)}>
                <StyledRemoveIcon />
              </DimensionRemove>
            )}
          </DimensionRow>
        ))}
      </Dimensions>
      {canAddDimension && (
        <AddDimensionRow>
          <AddDimension onClick={handleAddDimension}>
            <StyledAddIcon /> Add Question
          </AddDimension>
        </AddDimensionRow>
      )}
      <Actions>
        <AnalysisQueryHint
          disabled={!canContinue}
          value="Let's analyze these dimensions."
          onClick={handleContinue}
        />
        <CancelAction
          displayValue="Actually, I'd like to do something else."
          onClick={handleCancel}
        />
      </Actions>
    </Root>
  );
};

const Root = styled.div`
  box-sizing: border-box;
`;

const HeaderCopy = styled.div`
  box-sizing: border-box;
  border-bottom: 1px solid ${props => props.theme.palette.gray.light1};
  padding-bottom: 10px;
  margin-bottom: 10px;
`;

const PickCopy = styled.div`
  font-size: 15px;
  font-family: ${props => props.theme.fonts.semiBold};
  margin-bottom: 10px;
`;

const Dimensions = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  .dimension-input {
    width: 100%;
  }

  :not(:last-child) {
    margin-bottom: 10px;
  }
`;

const DimensionRow = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
`;

const DimensionRemove = styled.button`
  display: inline-flex;
  box-sizing: border-box;
  margin-left: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
`;

const StyledRemoveIcon = styled(RemoveCircleOutlineIcon)`
  color: ${props => props.theme.palette.text.primary};
`;

const AddDimensionRow = styled.div`
  box-sizing: border-box;
  padding: 0 5px;
`;

const AddDimension = styled.button`
  box-sizing: border-box;
  color: ${props => props.theme.palette.blue.main};
  display: inline-flex;
  align-items: center;
  font-family: ${props => props.theme.fonts.semiBold};
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
`;

const StyledAddIcon = styled(AddCircleOutlineIcon)`
  color: ${props => props.theme.palette.blue.main};
`;

const Actions = styled.div`
  margin-top: 15px;

  > :not(:last-child) {
    margin-bottom: 10px;
  }
`;
